import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, MapPin, Phone, Mail, Instagram, Facebook } from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { name: 'About', href: '#about' },
    { name: 'Programs', href: '#programs' },
    { name: 'Schedule', href: '#schedule' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Contact', href: '#contact' },
  ];

  const programs = [
    'Mixed Martial Arts',
    'Brazilian Jiu-Jitsu',
    'Muay Thai',
    'Boxing',
    'Kickboxing',
    'Kids Martial Arts',
  ];

  return (
    <footer className="relative bg-[#050505] border-t border-[#1A1A1A]" data-testid="footer">
      {/* Big CTA Text */}
      <div className="py-16 md:py-24 overflow-hidden">
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="font-['Oswald'] text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] font-bold text-center leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-[#DC2626] to-[#DC2626]/30"
        >
          TRAIN WITH US
        </motion.h2>
      </div>

      {/* Main Footer Content */}
      <div className="border-t border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Brand Column */}
            <div>
              <a href="#hero" className="inline-block mb-6">
                <span className="font-['Oswald'] text-3xl font-bold tracking-tight">
                  <span className="text-[#DC2626]">MMA</span>
                  <span className="text-white"> SDM</span>
                </span>
              </a>
              <p className="text-neutral-500 text-sm leading-relaxed mb-6">
                Mysuru's premier martial arts academy. Building champions through discipline, 
                strength, and professional training since day one.
              </p>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.instagram.com/sdm_mysuru"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-[#121212] border border-[#262626] hover:border-[#DC2626] hover:bg-[#DC2626] transition-all"
                >
                  <Instagram size={18} className="text-white" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 flex items-center justify-center bg-[#121212] border border-[#262626] hover:border-[#DC2626] hover:bg-[#DC2626] transition-all"
                >
                  <Facebook size={18} className="text-white" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-['Oswald'] text-white text-lg uppercase tracking-wider mb-6">
                Quick Links
              </h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-neutral-500 hover:text-white transition-colors text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Programs */}
            <div>
              <h4 className="font-['Oswald'] text-white text-lg uppercase tracking-wider mb-6">
                Programs
              </h4>
              <ul className="space-y-3">
                {programs.map((program) => (
                  <li key={program}>
                    <a
                      href="#programs"
                      className="text-neutral-500 hover:text-white transition-colors text-sm"
                    >
                      {program}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-['Oswald'] text-white text-lg uppercase tracking-wider mb-6">
                Contact Us
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#DC2626] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-500 text-sm">
                    #291/C Hebbal Industrial Area,<br />
                    Near Aroma Bakery, Opp to SPI,<br />
                    Mysore - 570016
                  </span>
                </li>
                <li>
                  <a href="tel:7899162820" className="flex items-center gap-3 text-neutral-500 hover:text-white transition-colors">
                    <Phone size={18} className="text-[#DC2626]" />
                    <span className="text-sm">+91 7899162820</span>
                  </a>
                </li>
                <li>
                  <a href="mailto:pramodlaxman1721@gmail.com" className="flex items-center gap-3 text-neutral-500 hover:text-white transition-colors">
                    <Mail size={18} className="text-[#DC2626]" />
                    <span className="text-sm">pramodlaxman1721@gmail.com</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-neutral-600 text-sm text-center md:text-left">
              © {new Date().getFullYear()} SDM School of Defense & Martial Arts. All rights reserved.
            </p>
            <button
              onClick={scrollToTop}
              className="flex items-center gap-2 text-neutral-500 hover:text-white transition-colors text-sm group"
              data-testid="scroll-to-top-btn"
            >
              <span>Back to Top</span>
              <ArrowUp size={16} className="group-hover:-translate-y-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
